/**
 * Read an arbitrary CSS colour back as a real sRGB pixel.
 *
 * `ctx.fillStyle = input; const result = ctx.fillStyle` is unreliable
 * across browsers — Chrome 130+ may return `color(srgb …)` notation,
 * older browsers may silently keep the previous fillStyle if the input
 * uses a colour space they don't understand. Both forms are unparseable
 * by `@kurkle/color` (which Chart.js and chartjs-chart-sankey use), so
 * we paint a single pixel and read it back through `getImageData`. That
 * is guaranteed to return numeric RGBA regardless of input colour space.
 */
function readPixel(input: string): { r: number; g: number; b: number; a: number } | null {
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = 1;
  c.height = 1;
  const ctx = c.getContext("2d");
  if (!ctx) return null;
  // Reset to a known-good state then assign the input. If the browser
  // can't parse the input, fillStyle stays "#000" — getImageData still
  // returns a well-formed (if wrong) value, so we don't crash. We could
  // detect parse failure by reading fillStyle first but it's safer to
  // always paint and read.
  ctx.fillStyle = "rgba(0, 0, 0, 0)";
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = input;
  ctx.fillRect(0, 0, 1, 1);
  const d = ctx.getImageData(0, 0, 1, 1).data;
  return { r: d[0]!, g: d[1]!, b: d[2]!, a: d[3]! / 255 };
}

function rgbaString(r: number, g: number, b: number, a: number): string {
  if (a >= 0.999) {
    const hex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${hex(r)}${hex(g)}${hex(b)}`;
  }
  return `rgba(${r}, ${g}, ${b}, ${a.toFixed(3)})`;
}

/**
 * Convert any CSS colour (oklch, lab, color(), hex, rgb, named) to an
 * `rgba(...)` string with the requested alpha. Always returns something
 * `@kurkle/color` can parse.
 */
export function withAlpha(input: string, alpha: number): string {
  if (!input) return `rgba(0, 0, 0, ${alpha})`;
  // Fast path: already a parseable sRGB notation, pass through (cheap).
  // (We still fall back to canvas if the alpha needs to change.)
  const px = readPixel(input);
  if (!px) return input;
  return `rgba(${px.r}, ${px.g}, ${px.b}, ${alpha})`;
}

/**
 * Normalize any CSS colour to a sRGB hex/rgba string the @kurkle/color
 * util (used internally by Chart.js and chartjs-chart-sankey) can parse.
 *
 * Modern brand tokens use oklch(), which @kurkle/color does NOT support
 * — passing raw oklch into chart.js (or worse, into Sankey's applyAlpha
 * helper) makes its Color parser return undefined and crashes downstream.
 *
 * Pipe palette and per-token colours through this once at the chart-theme
 * boundary; all downstream chart code then receives parseable values.
 */
export function normalizeColor(input: string): string {
  if (!input) return "#000000";
  const trimmed = input.trim();
  // Fast path: already a parseable form — return unchanged.
  if (/^#[0-9a-f]{3,8}$/i.test(trimmed)) return trimmed;
  if (/^(rgba?|hsla?|hwb|hsv)\(/i.test(trimmed)) return trimmed;
  // Named CSS colours: cheap allowlist to skip canvas — fall through to
  // canvas if unknown so the canvas can validate.
  const px = readPixel(trimmed);
  if (!px) return trimmed;
  return rgbaString(px.r, px.g, px.b, px.a);
}

/** Deep-merge two ChartOptions-like objects (arrays replaced, objects merged). */
export function mergeOptions<T>(a: T, b: Partial<T>): T {
  if (!b) return a;
  const out: Record<string, unknown> = { ...(a as object) };
  for (const k of Object.keys(b)) {
    const av = (a as Record<string, unknown>)[k];
    const bv = (b as Record<string, unknown>)[k];
    if (
      av && bv &&
      typeof av === "object" && typeof bv === "object" &&
      !Array.isArray(av) && !Array.isArray(bv)
    ) {
      out[k] = mergeOptions(av, bv as Partial<typeof av>);
    } else {
      out[k] = bv;
    }
  }
  return out as T;
}
